import React, { useState, useMemo } from "react";
import {
    Box,
    Fade,
    Typography,
    InputAdornment,
    TextField,
    Grid,
    Button,
    Stack,
    Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { uk, enUS } from "date-fns/locale";

export default function MeetingScheduleForm({ formData, setFormData, activeStep, handleBack, handleSubmit }) {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [isBackHovered, setIsBackHovered] = useState(false);
    const [isBackPressed, setIsBackPressed] = useState(false);
    const [isSubmitHovered, setIsSubmitHovered] = useState(false);
    const [isSubmitPressed, setIsSubmitPressed] = useState(false);

    // Time slots based on language
    const timeSlots = useMemo(() => {
        if (i18n.language === "ua") {
            return [
                "09:00", "10:00", "11:00", "12:00",
                "13:00", "14:00", "15:00", "16:00", "17:00"
            ];
        }
        return [
            "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
            "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM"
        ];
    }, [i18n.language]);

    const handleDateChange = (newDate) => {
        setFormData({
            ...formData,
            date: newDate,
        });
    };

    const datePickerLocale = useMemo(() => {
        return i18n.language === "ua" ? uk : enUS;
    }, [i18n.language]);

    return (
        <Fade in={activeStep === 1}>
            <Box
                sx={{
                    display: activeStep === 1 ? "flex" : "none",
                    flexDirection: "column",
                    gap: 2, // Reduced gap for mobile
                    flex: 1,
                    maxHeight: { xs: "80vh", sm: "auto" }, // Limit height on mobile
                    overflowY: { xs: "auto", sm: "visible" }, // Enable scroll on mobile
                    p: { xs: 2, sm: 3 }, // Adjust padding for mobile
                }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={datePickerLocale}>
                        <DatePicker
                            label={t("drawer.meeting.selectDatePlaceholder")}
                            value={formData.date}
                            onChange={handleDateChange}
                            disablePast
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    required
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EventNoteIcon sx={{ color: theme.palette.primary.main }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: theme.palette.background.paper,
                                            color: theme.palette.text.primary,
                                            "& fieldset": {
                                                borderColor: theme.palette.grey[300],
                                                borderWidth: 1,
                                            },
                                            "&:hover fieldset": {
                                                borderColor: theme.palette.primary.main,
                                                borderWidth: 2,
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: theme.palette.primary.main,
                                                borderWidth: 2,
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: theme.palette.text.secondary,
                                            "&.Mui-focused": {
                                                color: theme.palette.primary.main,
                                            },
                                            "&.MuiInputLabel-shrink": {
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                />
                            )}
                            sx={{
                                width: "100%",
                                "& .MuiDateCalendar-root": {
                                    backgroundColor: theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                    borderRadius: "12px",
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                },
                                "& .MuiPickersDay-root": {
                                    color: theme.palette.text.primary,
                                    fontWeight: 400,
                                    "&:hover": {
                                        backgroundColor: theme.palette.primary.main + '20',
                                        color: theme.palette.text.primary,
                                    },
                                    "&.Mui-selected": {
                                        backgroundColor: theme.palette.primary.main,
                                        color: "#FFFFFF",
                                        fontWeight: 600,
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary.dark,
                                        },
                                    },
                                    "&.Mui-disabled": {
                                        color: theme.palette.text.disabled,
                                    },
                                },
                                "& .MuiPickersCalendarHeader-root": {
                                    backgroundColor: theme.palette.grey[50],
                                    color: theme.palette.text.primary,
                                    "& .MuiPickersCalendarHeader-label": {
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                    },
                                },
                                "& .MuiDayPicker-weekDayLabel": {
                                    color: theme.palette.text.secondary,
                                    fontWeight: 500,
                                },
                                "& .MuiPickersArrowSwitcher-button": {
                                    color: theme.palette.text.primary,
                                    "&:hover": {
                                        backgroundColor: theme.palette.grey[100],
                                        color: theme.palette.primary.main,
                                    },
                                },
                                "& .MuiPickersYear-yearButton": {
                                    color: theme.palette.text.primary,
                                    "&:hover": {
                                        backgroundColor: theme.palette.grey[100],
                                    },
                                    "&.Mui-selected": {
                                        backgroundColor: theme.palette.primary.main,
                                        color: "#FFFFFF",
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary.dark,
                                        },
                                    },
                                },
                                "& .MuiPickersMonth-monthButton": {
                                    color: theme.palette.text.primary,
                                    "&:hover": {
                                        backgroundColor: theme.palette.grey[100],
                                    },
                                    "&.Mui-selected": {
                                        backgroundColor: theme.palette.primary.main,
                                        color: "#FFFFFF",
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary.dark,
                                        },
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <Typography
                        variant="h6"
                        sx={{ 
                            mb: 2, 
                            fontWeight: 600,
                            color: theme.palette.text.primary
                        }}
                    >
                        {t("drawer.meeting.availableTimeSlotsSectionTitle")}
                    </Typography>
                    <Grid container spacing={1}> {/* Reduced spacing for mobile */}
                        {timeSlots.map((time) => (
                            <Grid item xs={6} sm={4} key={time}>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button
                                        variant={formData.time === time ? "contained" : "outlined"}
                                        fullWidth
                                        onClick={() =>
                                            setFormData({
                                                ...formData,
                                                time,
                                            })
                                        }
                                        startIcon={<ScheduleIcon />}
                                        sx={{
                                            borderColor: theme.palette.grey[300],
                                            color: formData.time === time ? "#FFFFFF" : theme.palette.text.primary,
                                            backgroundColor: formData.time === time
                                                ? theme.palette.primary.main
                                                : "transparent",
                                            "&:hover": {
                                                borderColor: theme.palette.primary.main,
                                                backgroundColor: formData.time === time
                                                    ? theme.palette.primary.dark
                                                    : theme.palette.primary.main + '10',
                                                color: theme.palette.text.primary,
                                            },
                                            py: { xs: 1, sm: 1.2 },
                                            borderRadius: 8,
                                            fontSize: { xs: "0.9rem", sm: "1rem" },
                                            fontWeight: 500,
                                        }}
                                    >
                                        {time}
                                    </Button>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>

                <Divider sx={{ borderColor: theme.palette.grey[200], my: 1 }} />

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                >
                    <Box sx={{ 
                        backgroundColor: theme.palette.grey[50], 
                        p: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[200]}`
                    }}>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                mb: 1.5, 
                                fontWeight: 600,
                                color: theme.palette.text.primary
                            }}
                        >
                            {t("drawer.meeting.summaryTitle")}
                        </Typography>
                        <Stack spacing={1}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <PersonIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
                                <Typography sx={{ color: theme.palette.text.primary }}>{formData.name}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <EmailIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
                                <Typography sx={{ color: theme.palette.text.primary }}>{formData.email}</Typography>
                            </Box>
                            {formData.company && (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <BusinessIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
                                    <Typography sx={{ color: theme.palette.text.primary }}>{formData.company}</Typography>
                                </Box>
                            )}
                            {formData.topic && (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <EventNoteIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
                                    <Typography sx={{ color: theme.palette.text.primary }}>{formData.topic}</Typography>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                </motion.div>

                <Box sx={{ mt: "auto", pt: 2, display: "flex", gap: 1 }}> {/* Reduced gap for mobile */}
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} style={{ flex: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleBack}
                            onMouseEnter={() => setIsBackHovered(true)}
                            onMouseLeave={() => setIsBackHovered(false)}
                            onMouseDown={() => setIsBackPressed(true)}
                            onMouseUp={() => setIsBackPressed(false)}
                            sx={{
                                width: { xs: "100%", sm: "auto" },
                                fontSize: { xs: "0.9rem", sm: "1rem" },
                                px: 4,
                                py: 2,
                                fontWeight: 600,
                                borderRadius: 12,
                                textTransform: "none",
                                borderColor: theme.palette.grey[300],
                                color: theme.palette.text.primary,
                                "&:hover": {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: theme.palette.primary.main + '10',
                                },
                            }}
                        >
                            {t("drawer.meeting.backButton")}
                        </Button>
                    </motion.div>

                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} style={{ flex: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleSubmit}
                            onMouseEnter={() => setIsSubmitHovered(true)}
                            onMouseLeave={() => setIsSubmitHovered(false)}
                            onMouseDown={() => setIsSubmitPressed(true)}
                            onMouseUp={() => setIsSubmitPressed(false)}
                            disabled={!formData.date || !formData.time}
                            sx={{
                                width: { xs: "100%", sm: "auto" },
                                fontSize: { xs: "0.9rem", sm: "1rem" },
                                px: 4,
                                py: 2,
                                fontWeight: 600,
                                borderRadius: 12,
                                textTransform: "none",
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                color: "#FFFFFF",
                                boxShadow: isSubmitHovered
                                    ? `0 8px 24px ${theme.palette.primary.main}40`
                                    : `0 4px 12px ${theme.palette.primary.main}30`,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    boxShadow: `0 12px 32px ${theme.palette.primary.main}50`,
                                    transform: "translateY(-2px)",
                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                },
                            }}
                        >
                            {t("drawer.meeting.submitButton")}
                        </Button>
                    </motion.div>
                </Box>
            </Box>
        </Fade>
    );
}